<script>
import Vue from 'vue'
import { GlobalBus } from '@services/bus'
import {
  ACTION,
  MODAL,
  MODAL_IMPORT,
  RELOAD,
  TIPOS_COM_AMORTIZACAO,
  TIPOS_COM_CUPOM,
} from '@constants/Text'
import { possivelJSON } from '@utils/Format'
import { filter } from '@utils/Search'
import ComposicaoFundo from '@components/composicao-fundos'
import Title from '@components/section-title'
import { aawzDate } from '@utils/dates/aawz-date'
import { isFundType } from '@modules/consolidator/utils/asset-type-detection/asset-type-detection'
import { apiConsolidatorEvents } from '@modules/consolidator/api/apiConsolidatorEvents'
import { apiConsolidatorAssetSeries } from '@modules/consolidator/api/apiConsolidatorAssetSeries'
import { apiDetailsAsset } from '@modules/consolidator/api/apiDetailsAsset'
import AssetPerformanceChart from '@modules/consolidator/components/asset-performance-chart/asset-performance-chart.vue'
import {
  ASSET_CHART_DEFAULT_PERIOD,
  ASSET_CHART_HEIGHT,
  ASSET_CHART_PERIODS,
} from '@modules/consolidator/constants/AssetMarkers'
import { TIPO_PERSONALIZADO } from '@modules/consolidator/constants/AssetDetailsFicha'
import AssetHeader from '@modules/consolidator/components/details-asset/asset-header.vue'
import AssetKpis from '@modules/consolidator/components/details-asset/asset-kpis.vue'
import AssetFicha from '@modules/consolidator/components/details-asset/asset-ficha.vue'
import AssetTabMovimentacoes from '@modules/consolidator/components/details-asset/asset-tab-movimentacoes.vue'
import AssetTabEventos from '@modules/consolidator/components/details-asset/asset-tab-eventos.vue'
import AssetActionsFooter from '@modules/consolidator/components/details-asset/asset-actions-footer.vue'
import { openAssetSuggestions } from '@modules/consolidator/utils/asset-row-actions/asset-row-actions'
import { SUGESTOES_IA_VIEW } from '@modules/consolidator/constants/Text'
import {
  buildAssetView,
  formatDataOrDash,
  mapMovementsResponse,
} from '@modules/consolidator/components/details-asset/details-asset-format'

export default {
  components: {
    ComposicaoFundo,
    Title,
    AssetPerformanceChart,
    AssetHeader,
    AssetKpis,
    AssetFicha,
    AssetTabMovimentacoes,
    AssetTabEventos,
    AssetActionsFooter,
  },
  props: {
    busLeft: {
      type: Object,
      default: null,
    },
    bus: {
      type: Object,
      default: null,
    },
    editAtivo: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      busModal: new Vue(),
      loading: false,
      tab: null,
      asset: {},
      movimentacoes: [],
      proventos: [],
      splits: [],
      loadingEvents: false,
      sugestoesPorMovimentacao: null,
      movimentacoesDasSugestoes: null,
      showAlertFillPu: false,
      aviso_erros: [],
      actionsAlerts: ['movimentacoes'],
      clienteSelecionado: null,
      assetSeries: null,
      assetSeriesErro: false,
      serieEmVoo: '',
      benchmarksSelecionados: [],
      benchmarkEscolhidoPeloUsuario: false,
      loadingSeries: false,
      chartPeriod: ASSET_CHART_DEFAULT_PERIOD,
    }
  },
  computed: {
    alturaDoGrafico() {
      return `${ASSET_CHART_HEIGHT}px`
    },
    client() {
      return (
        this.clienteSelecionado ||
        this.$store.getters['user/currentClient'] ||
        {}
      )
    },
    usuarioAtual() {
      return this.$store.getters['user/currentUser'] || {}
    },
    movements() {
      return Object.values(this.movimentacoes).flatMap(
        (grupo) => grupo.movements
      )
    },
    hasCupom() {
      return TIPOS_COM_CUPOM.includes(this.asset.Tipo || this.asset.tipo)
    },
    hasAmortizacao() {
      return TIPOS_COM_AMORTIZACAO.includes(this.asset.Tipo || this.asset.tipo)
    },
    showISIN() {
      const subtipo = this.asset.subtipo
      return (
        (this.isRF && ['CRA', 'CRI', 'DEB'].includes(subtipo)) ||
        this.isOffshore
      )
    },
    isRF() {
      return ['renda fixa', 'tesouro direto'].includes(
        (this.asset['Tipo'] || this.asset.tipo || '').toLowerCase()
      )
    },
    isOffshore() {
      const tipo = (this.asset['Tipo'] || this.asset.tipo || '').toLowerCase()
      return tipo === 'offshore' || tipo === 'fundos offshore'
    },
    isFundo() {
      return isFundType(this.asset['Tipo'] || this.asset.tipo || '')
    },
    isPersonalizado() {
      return (this.asset['Tipo'] || this.asset.tipo) === TIPO_PERSONALIZADO
    },
    canShowSettings() {
      return this.$store.getters['parameters/show'].settings
    },
    camposFaltantesEntries() {
      if (
        !this.aviso_erros.length ||
        !this.editAtivo ||
        !this.asset.campos_faltantes
      ) {
        return []
      }
      return Object.entries(possivelJSON(this.asset.campos_faltantes) || {})
    },
    recotizacaoProcessando() {
      const clienteRecotizando =
        this.$store.getters['consolidator/recotizacaoEmAndamento']
      if (!clienteRecotizando) {
        return false
      }
      const idClienteAtivo = this.asset && this.asset.id_cliente
      if (!idClienteAtivo) {
        return Boolean(clienteRecotizando)
      }
      return clienteRecotizando === idClienteAtivo
    },
    hasEventos() {
      return [...(this.proventos || []), ...(this.splits || [])].length > 0
    },
    /** Predicados/formatadores compartilhados com header e ficha */
    fichaCtx() {
      return {
        isClient: Boolean(this.usuarioAtual.IS_CLIENTE),
        isRF: this.isRF,
        isOffshore: this.isOffshore,
        isFundo: this.isFundo,
        hasCupom: this.hasCupom,
        hasAmortizacao: this.hasAmortizacao,
        showISIN: this.showISIN,
        formatData: this.formatData,
      }
    },
    serieDoGrafico() {
      if (!this.assetSeries) return null
      const escolhidos = apiConsolidatorAssetSeries.seriesDeBenchmark({
        clienteId: this.client.id,
        beginDate: this.chartPeriodBeginDate(),
        nomes: this.benchmarksSelecionados,
        inicio: this.assetSeries.inicio,
      })
      if (Object.keys(escolhidos).length) {
        return { ...this.assetSeries, benchmark: escolhidos }
      }
      return { ...this.assetSeries, benchmark: this.benchmarksDoCache() }
    },
  },
  created() {
    GlobalBus.$on(MODAL.DETAILS_ASSET, this.open)
    GlobalBus.$on(RELOAD.MOVEMENTS_MODAL, this.collectMovements)
    this.busModal.$on('close', this.clearFields)
    GlobalBus.$on(RELOAD.MOVEMENTS, this.collectMovements)
    GlobalBus.$on(RELOAD.CONSOLIDATOR_ASSET_EVENT, this.reloadEvents)
    GlobalBus.$on(ACTION.UPDATE_ASSET, this.updateAsset)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.DETAILS_ASSET, this.open)
    GlobalBus.$off(RELOAD.MOVEMENTS_MODAL, this.collectMovements)
    this.busModal.$off('close', this.clearFields)
    GlobalBus.$off(RELOAD.MOVEMENTS, this.collectMovements)
    GlobalBus.$off(RELOAD.CONSOLIDATOR_ASSET_EVENT, this.reloadEvents)
    GlobalBus.$off(ACTION.UPDATE_ASSET, this.updateAsset)
  },
  methods: {
    clear() {
      this.showAlertFillPu = false
      this.aviso_erros = []
      this.tab = null
      this.movimentacoes = []
      this.splits = []
      this.proventos = []
      this.clienteSelecionado = null
      this.assetSeries = null
      this.assetSeriesErro = false
      this.serieEmVoo = ''
      this.benchmarksSelecionados = []
      this.benchmarkEscolhidoPeloUsuario = false
      this.loadingSeries = false
      this.chartPeriod = ASSET_CHART_DEFAULT_PERIOD
    },
    updateAsset(asset) {
      if (asset && asset.nome_ativo) {
        this.asset = structuredClone(asset)
      }
    },
    applyOpenOptions(opts) {
      if (!opts) {
        return
      }
      this.showAlertFillPu = opts.show_alert_fill_pu
      // Lista de ativos com um atributo sendo a lista de movimentacoes (movements)
      this.movimentacoes = opts.movimentacoes || []
      this.clienteSelecionado = opts.clienteSelecionado
      // A aba de Movimentacoes deste modal e a MESMA tabela do Extrato: sem isto ela era a unica
      // superficie do ativo sem o sinal da IA. Quem abre o modal ja tem a contagem apurada.
      this.sugestoesPorMovimentacao = opts.sugestoesPorMovimentacao || null
      // O de-para movimentacao->ativo vem junto: sem ele o painel aberto pelo selo desta aba nao
      // resolve o ativo de uma sugestao de movimentacao e abre vazio.
      this.movimentacoesDasSugestoes = opts.movimentacoesDasSugestoes || null
      if (opts.tab) {
        this.tab = opts.tab || 0
      }
    },
    applyOpenAsset(asset) {
      if (!asset) {
        return
      }
      this.asset = buildAssetView(asset)
      if (this.asset.campos_faltantes) {
        this.aviso_erros = Object.values(
          possivelJSON(this.asset.campos_faltantes) || {}
        )
      }
    },
    open(asset, opts) {
      this.clear()
      this.applyOpenOptions(opts)
      this.applyOpenAsset(asset)

      this.collectMovements()
      this.loadProfits()
      this.loadSplits()
      this.loadAssetSeries()

      this.busModal.$emit('dialog', true)
    },
    chartPeriodBeginDate() {
      const option = ASSET_CHART_PERIODS.find(
        (period) => period.key === this.chartPeriod
      )
      if (!option || !option.months) {
        return null
      }
      return aawzDate().add(-option.months, 'months').format('DD-MM-YYYY')
    },
    serieCacheada({ idAtivo, beginDate }) {
      return apiConsolidatorAssetSeries.lerSerie({
        isClient: Boolean(this.usuarioAtual.IS_CLIENTE),
        clienteId: this.client.id,
        idAtivo,
        beginDate,
      })
    },
    async fetchAssetSeries(idAtivo) {
      const r = await apiConsolidatorAssetSeries.carregar({
        isClient: this.usuarioAtual.IS_CLIENTE,
        clienteId: this.client.id,
        idAtivo,
        beginDate: this.chartPeriodBeginDate(),
      })
      const ok = !!(r && r.status === 200)
      return { serie: ok ? r.value : null, ok }
    },
    async buscarSerieDescartandoStale(idAtivo) {
      const pedido = `${idAtivo}:${this.chartPeriod}`
      this.serieEmVoo = pedido
      this.loadingSeries = true
      try {
        const { serie, ok } = await this.fetchAssetSeries(idAtivo)
        if (this.serieEmVoo !== pedido) return
        this.assetSeries = serie
        this.aplicarBenchmarkPadrao(serie)
        this.assetSeriesErro = !ok
      } finally {
        if (this.serieEmVoo === pedido) this.loadingSeries = false
      }
    },
    benchmarksDoCache() {
      const guardado = (this.assetSeries && this.assetSeries.benchmark) || {}
      const doCache = {}
      for (const nome of this.benchmarksSelecionados) {
        if (guardado[nome]) doCache[nome] = guardado[nome]
      }
      return doCache
    },
    onBenchmarksChange(nomes) {
      this.benchmarksSelecionados = nomes
      this.benchmarkEscolhidoPeloUsuario = true
    },
    aplicarBenchmarkPadrao(serie) {
      if (!serie || this.benchmarkEscolhidoPeloUsuario) return
      this.benchmarksSelecionados = [serie.benchmark_nome]
    },
    clearSeries() {
      this.assetSeries = null
      this.assetSeriesErro = false
    },
    applyCachedSeries(cacheado) {
      this.assetSeries = cacheado.value
      this.aplicarBenchmarkPadrao(cacheado.value)
      this.assetSeriesErro = false
      this.serieEmVoo = ''
      this.loadingSeries = false
    },
    async loadAssetSeries() {
      const idAtivo = this.asset.id
      if (!idAtivo) {
        this.clearSeries()
        return
      }
      const cacheado = this.serieCacheada({
        idAtivo,
        beginDate: this.chartPeriodBeginDate(),
      })
      if (cacheado) {
        this.applyCachedSeries(cacheado)
        return
      }
      await this.buscarSerieDescartandoStale(idAtivo)
    },
    onChartPeriodChange(periodKey) {
      this.chartPeriod = periodKey
      this.loadAssetSeries()
    },
    onMarkerClick(raw) {
      if (!raw) {
        return
      }
      this.tab = raw.targetTab === 'eventos' && this.hasEventos ? 2 : 1

      const itens = Array.isArray(raw.items) ? raw.items : []
      if (itens.length !== 1) {
        return
      }
      this.abrirEditorDoItem({ item: itens[0], targetTab: raw.targetTab })
    },
    abrirEditorDoItem({ item, targetTab } = {}) {
      if (!item) {
        return
      }
      if (targetTab === 'eventos') {
        this.emitirEditorDeEvento({
          item,
          eventos: (item.split ? this.splits : this.proventos) || [],
        })
        return
      }
      if (targetTab !== 'movimentacoes' || !this.busLeft) {
        return
      }
      this.busLeft.$emit(
        MODAL.CADASTRAR_APORTE,
        { id: this.client.id, name: this.client.nome_cliente },
        item,
        this.asset
      )
    },
    emitirEditorDeEvento({ item, eventos }) {
      GlobalBus.$emit(MODAL.CONSOLIDATOR_ASSET_EVENT, {
        item,
        ativo: this.asset,
        eventos,
        callback: () => {
          GlobalBus.$emit(RELOAD.CONSOLIDATOR_ASSET_EVENT, true)
        },
      })
    },
    clearFields() {
      this.asset = {}
    },
    async editAsset(asset, close) {
      if (this.editAtivo) {
        await this.editAtivo(asset)
      }
    },
    applyMovementsResponse(value) {
      const mapped = mapMovementsResponse(value)
      this.movimentacoes = filter(
        Object.values(possivelJSON(mapped)),
        'id',
        this.asset.id
      )

      this.asset.quantidade_total = 0
      Object.values(this.movimentacoes).forEach((v) => {
        v.movements.forEach((movement) => {
          this.asset.quantidade_total =
            movement.quantidade + this.asset.quantidade_total
        })
      })

      if (this.showAlertFillPu) {
        const needFillMovements = this.movements.some((item) => {
          return !item.valor_unitario
        })
        if (!needFillMovements) {
          this.showAlertFillPu = false
        }
      }
    },
    async collectMovements(force_update = false) {
      const temMovimentacoes =
        this.movimentacoes.length &&
        this.movimentacoes.some((item) => {
          return item.id === this.asset.id
        })
      if (temMovimentacoes && !force_update) {
        // Filtrar para ter certeza que sao as movimentacoes certas
        this.movimentacoes = filter(this.movimentacoes, 'id', this.asset.id)
        return
      }
      const clienteId = this.client.id || this.$route.params.id_cliente
      if (!clienteId) {
        return
      }
      this.loading = true
      const r = await apiDetailsAsset.listMovementsGroupedByAsset({
        isClient: this.usuarioAtual.IS_CLIENTE,
        clienteId,
      })
      if (r.status === 200) {
        this.applyMovementsResponse(r.value)
      } else {
        this.movimentacoes = []
      }
      this.loading = false
    },
    async reloadEvents() {
      if (this.loadingEvents) {
        return
      }
      this.loadingEvents = true
      try {
        await Promise.all([
          this.loadProfits({ forceUpdate: true }),
          this.loadSplits({ forceUpdate: true }),
        ])
      } finally {
        this.loadingEvents = false
      }
    },
    async loadProfits({ forceUpdate = false } = {}) {
      const isCliente = this.$currentUser().IS_CLIENTE
      if (!isCliente && !this.client.id) {
        return
      }

      await apiConsolidatorEvents.carregarProventos({
        idCliente: this.client.id,
        isCliente,
        forceUpdate,
      })

      const map = this.$store.getters['consolidator/proventos']
      this.proventos = (map[this.asset.id] || []).map((event) => {
        const copy = { ...event }
        if (!event.evento) {
          copy.operacao = this.hasCupom ? 'Cupom' : 'Proventos'
        }
        return copy
      })
    },
    async loadSplits({ forceUpdate = false } = {}) {
      const isCliente = this.$currentUser().IS_CLIENTE
      if (!isCliente && !this.client.id) {
        return
      }

      await apiConsolidatorEvents.carregarSplits({
        idCliente: this.client.id,
        isCliente,
        forceUpdate,
      })

      const map = this.$store.getters['consolidator/splits']
      this.splits = map[this.asset.id] || []
    },
    openModalRemove(asset) {
      this.$showModal({
        type: 'danger',
        v2: true,
        center: true,
        title: 'Remover movimentação',
        text: 'Tem certeza que deseja remover a movimentação do ativo?',
        buttons: [
          { text: 'Cancelar', action: () => {} },
          { text: 'Remover', action: () => this.removeMovimentacao(asset) },
        ],
      })
    },
    async removeMovimentacao(movement) {
      this.loading = true
      const r = await apiDetailsAsset.removeMovement({
        isClient: this.usuarioAtual.IS_CLIENTE,
        movement,
      })
      this.$showSnack({
        text: r.message,
        button: true,
      })
      this.loading = false
      GlobalBus.$emit(RELOAD.MOVEMENTS, true)
    },
    // O selo desta aba prometia "Clique para revisar" e nao tinha ouvinte nenhum: o evento subia
    // ate a tabela e morria. Mesmo destino das outras superficies — o painel do ATIVO.
    abrirSugestoesDaMovimentacao() {
      const aberto = openAssetSuggestions(
        this.asset,
        [],
        this.movimentacoesDasSugestoes,
        this.clienteSelecionado || this.client
      )
      if (aberto) return
      this.$showSnack({
        text: SUGESTOES_IA_VIEW.semClienteNoAtivo,
        color: 'error',
        button: true,
      })
    },
    openMovimentacoes(asset, close) {
      this.busLeft.$emit(
        MODAL.CADASTRAR_APORTE,
        { id: this.client.id, name: this.client.nome_cliente },
        '',
        asset,
        false
      )
    },
    openSerieHistorica(asset) {
      GlobalBus.$emit(MODAL_IMPORT.MANUAL_QUOTAS, {
        asset: asset,
        cliente: this.client,
        forceBaseScope: true,
      })
    },
    formatData(d) {
      return formatDataOrDash(d)
    },
    acaoAviso(val) {
      if (val === 'movimentacoes') {
        this.openMovimentacoes(this.asset)
      }
    },
    editarComposicao(asset) {
      this.$router.push({
        name: 'settings.assets',
        params: {
          editarComposicao: asset,
        },
      })
    },
    openAssetEvents() {
      this.emitirEditorDeEvento({
        item: {
          origem: 'Manual',
          nome_ativo: this.asset.nome_ativo,
        },
        eventos: this.proventos,
      })
    },
  },
}
</script>

<template>
  <BaseModal :bus="busModal" max-width="80%" fixedActions>
    <template v-slot:title>
      <div class="mb-4">
        <Title
          title="Ver detalhes"
          :subtitle="asset.nome_ativo || ''"
          underlined
        />
        <AssetHeader class="mt-2" :asset="asset" :ctx="fichaCtx" />
      </div>
    </template>

    <div v-if="loading" class="details-asset details-asset__skeleton p-5">
      <BaseLoadingSkeleton height="96px" class="mb-4" />
      <BaseLoadingSkeleton :height="alturaDoGrafico" class="mb-4" />
      <BaseLoadingSkeleton height="180px" />
    </div>

    <!-- Detalhes do ativo -->
    <div v-if="!loading" class="details-asset p-5">
      <BaseAlert
        v-if="recotizacaoProcessando"
        icon="info"
        icon-color="info"
        class="my-2"
      >
        <span class="text-normal">
          A cotização deste ativo está sendo processada. Os avisos serão
          atualizados em instantes.
        </span>
      </BaseAlert>
      <template v-else>
        <BaseAlert
          v-for="(aviso, ind) in camposFaltantesEntries"
          :key="'aviso-' + ind"
          icon="warning"
          icon-color="warning"
          class="my-2"
        >
          <span class="text-normal">{{ aviso[1] }}</span>
          <template v-if="actionsAlerts.includes(aviso[0])" v-slot:button>
            <BaseButton
              small
              dark
              outline
              min-width="80px"
              class="mx-10"
              @click="acaoAviso(aviso[0])"
            >
              <icon-svg name="plus" :size="16" />
              Movimentação
            </BaseButton>
          </template>
        </BaseAlert>
      </template>

      <AssetKpis
        :asset="asset"
        :serie="assetSeries"
        :loading="loadingSeries"
      />

      <AssetPerformanceChart
        :key="asset.id"
        class="mb-4"
        :series="serieDoGrafico"
        :movements="movements"
        :proventos="proventos"
        :splits="splits"
        :loading="loadingSeries"
        :erro="assetSeriesErro"
        :period="chartPeriod"
        :benchmarks-selecionados="benchmarksSelecionados"
        :moeda="asset.moeda || ''"
        @change-benchmarks="onBenchmarksChange"
        @period-change="onChartPeriodChange"
        @marker-click="onMarkerClick"
      />

      <div>
        <v-tabs v-model="tab">
          <v-tab key="Dados Gerais"> Dados Gerais</v-tab>
          <v-tab key="Movimentações">Movimentações</v-tab>
          <v-tab v-if="hasEventos" key="Eventos">Eventos</v-tab>
          <v-tab
            v-if="isFundo && asset.id_ativo"
            key="Composição fundo"
            href="#composicao"
          >
            Composição
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab" class="details-asset__tabs-content">
          <v-tab-item key="Dados Gerais">
            <AssetFicha class="mt-3" :asset="asset" :ctx="fichaCtx" />
          </v-tab-item>
          <v-tab-item key="Movimentações">
            <AssetTabMovimentacoes
              :sugestoes-por-movimentacao="sugestoesPorMovimentacao"
              :asset="asset"
              :client="client"
              :bus-left="busLeft"
              :movimentacoes="movimentacoes"
              :movements="movements"
              :show-alert-fill-pu="showAlertFillPu"
              @remove-movimentacao="openModalRemove"
              @open-movimentacoes="openMovimentacoes"
              @revisar-sugestoes="abrirSugestoesDaMovimentacao"
            />
          </v-tab-item>

          <v-tab-item v-if="hasEventos" key="Eventos">
            <AssetTabEventos
              :asset="asset"
              :proventos="proventos"
              :splits="splits"
              :loading="loadingEvents"
            />
          </v-tab-item>

          <v-tab-item
            v-if="isFundo && asset.id_ativo"
            key="Composição fundo"
            value="composicao"
          >
            <div class="details-asset__composicao">
              <div class="d-flex mt-2">
                <icon-svg name="mdi-information-outline" :size="16" />
                <p class="mt-2 mb-2">
                  <small>
                    As informações de rentabilidade e volatilidade utilizadas
                    são sempre do ativo macro, ou seja, não são detalhadas
                    conforme os produtos da composição do ativo.
                  </small>
                </p>
              </div>
              <ComposicaoFundo :ativo="asset" />
            </div>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </div>

    <template v-slot:actions="{ close }">
      <AssetActionsFooter
        class="mt-4"
        :asset="asset"
        :show-serie-historica="isPersonalizado && !$currentUser().IS_CLIENTE"
        :show-edit-ativo="Boolean(editAtivo)"
        :show-edit-composicao="
          isFundo &&
          Boolean(asset.id_ativo) &&
          $isWhitelabel() &&
          canShowSettings &&
          !$currentUser().IS_CLIENTE
        "
        @close="close"
        @open-serie-historica="openSerieHistorica"
        @edit-asset="editAsset"
        @edit-composicao="editarComposicao"
        @open-asset-events="openAssetEvents"
        @open-movimentacoes="openMovimentacoes"
      />
    </template>
  </BaseModal>
</template>

<style lang="sass" scoped>
.details-asset
  &__tabs-content
    min-height: 260px
    overflow-x: hidden

  &__composicao
    max-height: 100%
</style>
